import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import type { Security, useDisclosureWithContext } from '@talos/kyoko';
import { BlotterTable, Box, useBlotterObservables, useBlotterTable } from '@talos/kyoko';
import type { CellClassParams, CellEditRequestEvent } from 'ag-grid-community';
import { set } from 'lodash-es';
import { memo, useCallback } from 'react';
import {
  type DealProductBuilderColumn,
  type DealProductBuilderFieldDefinitions,
  useDealProductBuilderColDef,
} from '../dealProductBuilderColDef';
import { useDealProductBuilder } from '../DealProductBuilderProvider/useDealProductBuilder';
interface DealProductBuilderFormProps {
  panelID: string;
  security?: Security;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
  customColumnDefs: DealProductBuilderColumn[];
  fieldDefinitions: DealProductBuilderFieldDefinitions;
}

const DEFAULT_COLUMN_DEFINITION = {
  suppressMovable: true,
  cellClassRules: {
    editable: (params: CellClassParams) => !!params.colDef?.editable,
  },
  suppressHeaderMenuButton: true,
};

export const DealProductBuilderGrid = memo(
  ({ productDetailsDisclosure, customColumnDefs, fieldDefinitions }: DealProductBuilderFormProps) => {
    const {
      state,
      dispatch,
      actions: { updateGridRowData: updateShallowRowData, clearError },
    } = useDealProductBuilder();
    const { rowData } = state;

    const onRowUpdate = useCallback(
      (rowIndex: number, data: DealProductBuilderRow) => {
        dispatch(clearError());
        dispatch(updateShallowRowData({ rowIndex, data }));
      },
      [dispatch, clearError, updateShallowRowData]
    );

    const columns = useDealProductBuilderColDef(productDetailsDisclosure, customColumnDefs, fieldDefinitions);

    const handleCellEditRequest = useCallback(
      (event: CellEditRequestEvent<DealProductBuilderRow, unknown>) => {
        const { rowIndex, data, column, newValue } = event;
        if (rowIndex == null || !data) {
          return;
        }

        const field = column.getColDef().field;
        if (!field) {
          return;
        }

        // We support either a straight value, or an object with a value property within.
        // This is defined by the columns themselves, so they need to be conformant.
        let nextValue: unknown =
          typeof newValue === 'object' && newValue != null && 'value' in newValue ? newValue.value : newValue;

        if (field === 'uniqueSuffix' && nextValue === '') {
          nextValue = null;
        }

        const updatedData = { ...data };
        set(updatedData, field, nextValue);
        onRowUpdate(rowIndex, updatedData);
      },
      [onRowUpdate]
    );

    const { dataObservable } = useBlotterObservables({
      data: rowData,
    });

    const blotterTable = useBlotterTable<DealProductBuilderRow>({
      dataObservable,
      errorObservable: undefined, // Form grid, errors shown within the form
      // readOnlyEdit + onCellEditRequest: default applyTransactionAsync can still apply a stale
      // "typing" update after a later row emission (e.g. Escape rollback in textLiveEdit), clobbering it.
      applyTransactionsSynchronously: true,
      rowID: 'id',
      columns,
      gridOptions: {
        defaultColDef: DEFAULT_COLUMN_DEFINITION,
        singleClickEdit: true,
        readOnlyEdit: true,
        rowHeight: 34,
        onCellEditRequest: handleCellEditRequest,
        suppressContextMenu: true,
      },
    });

    return (
      <Box minHeight={110} h={110}>
        <BlotterTable {...blotterTable} />
      </Box>
    );
  }
);
